import React, { useState, useCallback, useEffect } from 'react';
import {
  Edit,
  SimpleForm,
  TextInput,
  NumberInput,
  required,
  TopToolbar,
  ListButton,
  ShowButton,
  useEditContext,
  useDataProvider,
} from 'react-admin';
import { Card, Typography, Box, Divider, Alert } from '@mui/material';
import {
  DataObject as VariableIcon,
  List as ListIcon,
  Visibility as ShowIcon,
} from '@mui/icons-material';
import HierarchicalFilter, {
  HierarchicalFilterSelections,
} from '../../components/HierarchicalFilter';

const VariableEditActions = () => (
  <TopToolbar>
    <ListButton icon={<ListIcon />} />
    <ShowButton icon={<ShowIcon />} />
  </TopToolbar>
);

const useVariableEditTransform = (filterValues, record) => {
  return data => {
    const tableId = filterValues.tableId || record?.tableId;
    if (!tableId) {
      throw new Error('Por favor selecciona una tabla válida antes de guardar');
    }
    const payload = {
      id: record?.id,
      variableName: data.variableName,
      varType: 'SCADA',
      description: data.description || null,
      scadaReferenceName: data.scadaReferenceName,
      metricsInterval: data.metricsInterval > 0 ? data.metricsInterval : null,
      tableId,
    };
    console.log('Transform - Payload de actualización:', payload);
    return payload;
  };
};

const VariablesEdit = props => {
  const [filterValues, setFilterValues] =
    useState<HierarchicalFilterSelections>({});
  return (
    <Edit {...props} actions={<VariableEditActions />}>
      <VariablesEditContent
        filterValues={filterValues}
        setFilterValues={setFilterValues}
      />
    </Edit>
  );
};

const VariablesEditContent = ({ filterValues, setFilterValues }) => {
  const { record } = useEditContext();
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(false);
  const dataProvider = useDataProvider();

  const transform = useVariableEditTransform(filterValues, record);

  const handleFilterChange = useCallback(
    (selections: HierarchicalFilterSelections) => {
      setFilterValues(selections);
    },
    [setFilterValues]
  );

  useEffect(() => {
    const loadInitialFilterData = async () => {
      if (record && record.tableId && !filterValues.tableId) {
        setIsLoadingInitialData(true);
        try {
          const tableRes = await dataProvider.getOne('tables', {
            id: record.tableId,
          });
          const table = tableRes.data;

          if (table?.zoneId) {
            const zoneRes = await dataProvider.getOne('zones', {
              id: table.zoneId,
            });
            const zone = zoneRes.data;

            if (zone?.plantId) {
              const plantRes = await dataProvider.getOne('plants', {
                id: zone.plantId,
              });
              const plant = plantRes.data;

              setFilterValues({
                plantId: zone.plantId,
                plantName: plant?.plantName || `Planta ${zone.plantId}`,
                zoneId: table.zoneId,
                zoneName: zone?.zoneName || `Zona ${table.zoneId}`,
                tableId: record.tableId,
                tableName: table?.tableName || `Tabla ${record.tableId}`,
              });
            } else {
              setFilterValues({
                zoneId: table.zoneId,
                zoneName: zone?.zoneName || `Zona ${table.zoneId}`,
                tableId: record.tableId,
                tableName: table?.tableName || `Tabla ${record.tableId}`,
              });
            }
          } else {
            setFilterValues({
              tableId: record.tableId,
              tableName: table?.tableName || `Tabla ${record.tableId}`,
            });
          }
        } catch (err) {
          console.error('Error al cargar jerarquía:', err);
          setFilterValues({ tableId: record.tableId });
        } finally {
          setIsLoadingInitialData(false);
        }
      }
    };

    loadInitialFilterData();
  }, [record?.tableId, dataProvider, filterValues.tableId]);

  if (!record) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Alert severity='info'>Cargando datos de la variable...</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 3, margin: '0 auto' }}>
      <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
        <Box sx={{ p: 3 }}>
          <Box display='flex' alignItems='center' mb={3}>
            <VariableIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
            <Box>
              <Typography variant='h5' fontWeight='bold'>
                Editar Variable SCADA
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Modifica los datos de la variable SCADA
              </Typography>
            </Box>
          </Box>

          {/* Sección de Filtro Jerárquico */}
          <Card sx={{ p: 3, mb: 3, backgroundColor: 'background.default' }}>
            <Typography variant='h6' gutterBottom color='primary'>
              Tabla Actual y Selección
            </Typography>

            {isLoadingInitialData && (
              <Alert severity='info' sx={{ mb: 2 }}>
                Cargando información de jerarquía...
              </Alert>
            )}

            {!isLoadingInitialData && record.tableId && (
              <Alert severity='info' sx={{ mb: 2 }}>
                Variable actualmente asignada a la tabla con ID:{' '}
                {record.tableId}
                {filterValues.plantName &&
                  filterValues.zoneName &&
                  filterValues.tableName && (
                    <>
                      {' '}
                      ({filterValues.plantName} → {filterValues.zoneName} →{' '}
                      {filterValues.tableName})
                    </>
                  )}
              </Alert>
            )}

            <HierarchicalFilter
              levels={['plant', 'zone', 'table']}
              onChange={handleFilterChange}
              initialValues={filterValues}
              size='medium'
              disabled={isLoadingInitialData}
              useTableSelector={true}
            />

            {filterValues.tableId !== record.tableId && (
              <Alert severity='warning' sx={{ mt: 2 }}>
                ⚠️ Cambiando la tabla de ID {record.tableId} a ID{' '}
                {filterValues.tableId}
              </Alert>
            )}

            {filterValues.tableId === record.tableId &&
              !isLoadingInitialData && (
                <Alert severity='success' sx={{ mt: 2 }}>
                  ✓ Tabla actual:
                  {filterValues.plantName &&
                    filterValues.zoneName &&
                    filterValues.tableName && (
                      <>
                        {' '}
                        {filterValues.plantName} → {filterValues.zoneName} →{' '}
                        {filterValues.tableName}
                      </>
                    )}
                </Alert>
              )}
          </Card>

          {/* Formulario */}
          <SimpleForm
            transform={transform}
            sx={{
              '& .RaSimpleForm-toolbar': {
                marginTop: 3,
                padding: 2,
                backgroundColor: 'background.paper',
                borderRadius: 2,
                boxShadow: 2,
              },
            }}
          >
            <Card sx={{ p: 3, mb: 3, backgroundColor: 'background.default' }}>
              <Typography variant='h6' gutterBottom color='primary'>
                Información de la Variable SCADA
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <TextInput
                source='variableName'
                label='Nombre de la Variable'
                validate={[required()]}
                fullWidth
                sx={{ mb: 3 }}
              />

              <TextInput
                source='scadaReferenceName'
                label='Referencia SCADA'
                validate={[required()]}
                fullWidth
                sx={{ mb: 3 }}
              />

              <TextInput
                source='description'
                label='Descripción (Opcional)'
                multiline
                rows={3}
                fullWidth
                sx={{ mb: 3 }}
              />

              <NumberInput
                source='metricsInterval'
                label='Intervalo de Métricas (ms)'
                fullWidth
                sx={{ mb: 2 }}
              />

              <Alert severity='info' sx={{ mt: 2 }}>
                <Typography variant='body2'>
                  <strong>Tipo de variable:</strong> SCADA (no se puede cambiar)
                </Typography>
                <Typography variant='body2'>
                  <strong>ID:</strong> {record.id}
                </Typography>
              </Alert>
            </Card>
          </SimpleForm>
        </Box>
      </Card>
    </Box>
  );
};

export default VariablesEdit;
